import { Badge, Card, Grid, Text } from '@mantine/core';
import { IconAlertTriangle, IconBolt, IconHistory, IconSettings, IconUsers } from '@tabler/icons-react';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import prettifiedLabel from '../../constants/labelPrettifier';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import Spinner from '../Spinner';

const fetchDebounced = AwesomeDebouncePromise((fetch, accountId, domainId, filter) => {
  return fetch(
    `/api/admin/accounts/${accountId}/domains/${domainId}/campaigns?${queryString.stringify({ ...filter })}`
  );
}, 300);

const MarketingChannelsFlowCampaignCards = ({ element, type, searchTerms }) => {
  const fetch = useFetch();
  const { accountId, domainId } = useParams();
  const [campaigns, setCampaigns] = useState();
  const [loadError, setLoadError] = useState();

  useEffect(() => {
    const filter = {
      page: 1,
      resultsPerPage: 10000,
      type: 'simple',
      searchTerms,
      ...(type === 'Facebook Ad' && { mode: 'oneOff' }),
      // Google Ads are continuous-only now, so list continuous campaigns to attach to.
      ...(type === 'Google Ad' && { mode: 'continuous' }),
    };
    setCampaigns(undefined);
    fetchDebounced(fetch, accountId, domainId, filter)
      .then(({ campaigns }) => {
        setCampaigns([...campaigns]);
      })
      .catch(setLoadError);
  }, [fetch, accountId, domainId, searchTerms, type]);

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (campaigns === undefined) {
    // Render a loading indicator while data is fetched
    return <Spinner />;
  }

  return (
    <>
      {campaigns.map((campaign) => (
        <Card key={campaign.id} className="card" ml={10} mr={10} mb="lg" withBorder>
          <Card.Section inheritPadding py="sm" ta="left" bg="white">
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <div>
                  <a
                    href={`#/accounts/${accountId}/domains/${domainId}/campaigns/${campaign.id}/details?mode=${campaign.mode}&flowType=${type}&flowElementId=${element.id}`}
                  >
                    <strong>{campaign.name}</strong>
                  </a>
                </div>
                <div>
                  <Badge variant="default">
                    <IconUsers size={14} />
                    &nbsp;
                    <a
                      rel="noreferrer noopener"
                      href={
                        campaign.segment
                          ? `#/accounts/${accountId}/domains/${domainId}/segments/${campaign.segment.id}`
                          : null
                      }
                      target="_blank"
                    >
                      {campaign.segment?.name || 'Unknown'}
                    </a>
                  </Badge>
                  <Badge variant="default">{prettifiedLabel(campaign.mode)}</Badge>
                  {campaign.mode === 'triggered' && (
                    <Badge variant="default">
                      {campaign.trigger.type === 'event' && (
                        <>
                          <IconBolt size={14} /> {prettifiedLabel(campaign.trigger.action)}
                        </>
                      )}
                      {campaign.trigger.type === 'webhook' && (
                        <>
                          <IconSettings size={14} /> Webhook
                        </>
                      )}
                    </Badge>
                  )}
                  {campaign.useOptimalTime ? (
                    <Badge variant="default">
                      <IconHistory size={14} /> Optimal Time
                    </Badge>
                  ) : null}
                  {campaign.scope === 'transactional' ? (
                    <Badge variant="default">
                      <IconAlertTriangle size={14} /> Transactional
                    </Badge>
                  ) : null}
                  {campaign.scope === 'optOut' ? (
                    <Badge variant="default">
                      <IconAlertTriangle size={14} /> Opt-out Marketing
                    </Badge>
                  ) : null}
                </div>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <div className="status-holder">
                  <Text size="sm" c="dimmed">
                    Created / Updated:
                  </Text>
                  <Text size="sm" c="dimmed">
                    <span>{new Date(campaign.createdAt).toLocaleString()}</span> -
                    <span>{new Date(campaign.updatedAt).toLocaleString()}</span>
                  </Text>
                </div>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 2 }}>
                <div className="status-holder">
                  <Text size="sm" c="dimmed">
                    Scheduled:
                  </Text>
                  <Text size="sm" c="dimmed">
                    <span>{campaign.scheduledAt ? new Date(campaign.scheduledAt).toLocaleString() : ''}</span>
                  </Text>
                </div>
              </Grid.Col>
            </Grid>
          </Card.Section>
        </Card>
      ))}
    </>
  );
};

export default MarketingChannelsFlowCampaignCards;
